import type { ReactNode } from 'react';
import { Button } from '@heroui/react';
import { Plus } from 'lucide-react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import SortablePanelRow from './SortablePanelRow';

interface PanelListSectionProps<T> {
  title: string;
  /** Optional line under the title explaining what the numbers mean. */
  subtitle?: ReactNode;
  items: T[];
  /** Stable key per item, same length and order as `items`. */
  itemKeys: string[];
  emptyLabel: string;
  onAdd: () => void;
  onReorder: (from: number, to: number) => void;
  /** The row's own controls. */
  renderRow: (item: T, index: number) => ReactNode;
  /** Right-hand qty note for the row, or omitted for no note. */
  renderNote?: (item: T, index: number) => ReactNode;
}

/**
 * Header + reorderable panel rows + empty state. Shared by all four framing
 * editors, each of which renders it twice (rails, additional panels).
 *
 * Each list owns its own DndContext, so rows reorder within their list and
 * cannot cross into the other — the two lists hold different fields, and a
 * cross-list drop would silently drop data.
 */
export default function PanelListSection<T>({
  title,
  subtitle,
  items,
  itemKeys,
  emptyLabel,
  onAdd,
  onReorder,
  renderRow,
  renderNote,
}: PanelListSectionProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = itemKeys.indexOf(String(active.id));
    const to = itemKeys.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    onReorder(from, to);
  };

  return (
    <div>
      <div className={`flex items-center justify-between ${subtitle ? 'mb-0.5' : 'mb-2'}`}>
        <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>
          {title}
        </span>
        <Button size="sm" variant="light" onPress={onAdd} startContent={<Plus size={12} />}>
          Add Panel
        </Button>
      </div>
      {subtitle && (
        <div className="text-[10px] mb-2" style={{ color: 'var(--text-muted)' }}>
          {subtitle}
        </div>
      )}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={itemKeys} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {items.map((item, i) => (
              <SortablePanelRow key={itemKeys[i]} id={itemKeys[i]} note={renderNote?.(item, i)}>
                {renderRow(item, i)}
              </SortablePanelRow>
            ))}
          </div>
        </SortableContext>
      </DndContext>
      {items.length === 0 && (
        <div className="text-xs text-center py-2" style={{ color: 'var(--text-muted)' }}>
          {emptyLabel}
        </div>
      )}
    </div>
  );
}
